"use client";

import { startTransition } from "react";

import type { KundaliLang } from "./KundaliFormCard.i18n";

export const LOADING_STEPS = [
  { en: "Finding your birth location...", hi: "आपका जन्म स्थान ढूंढ रहे हैं..." },
  { en: "Calculating planetary positions...", hi: "ग्रहों की स्थिति की गणना कर रहे हैं..." },
  { en: "Detecting doshas & yogas...", hi: "दोष और योग का विश्लेषण कर रहे हैं..." },
  { en: "Generating personalized reading...", hi: "व्यक्तिगत भविष्यवाणी तैयार कर रहे हैं..." },
];

export const STEP_DELAY_MS = 1500;

// Schedules the loading-step advance timers. Lives here (next to LOADING_STEPS +
// STEP_DELAY_MS) so the form card stays under the executable-LOC ceiling (G03).
export function loadingTimers(setLoadingStep: (step: number) => void) {
  const timers: ReturnType<typeof setTimeout>[] = [];
  for (let i = 1; i < LOADING_STEPS.length; i++) {
    timers.push(setTimeout(() => startTransition(() => setLoadingStep(i)), STEP_DELAY_MS * i));
  }
  return timers;
}

interface KundaliLoadingStepsProps {
  loadingStep: number;
  language: KundaliLang;
}

export function KundaliLoadingSteps({ loadingStep, language }: KundaliLoadingStepsProps) {
  return (
    <div className="space-y-2 pt-1">
      {LOADING_STEPS.map((step, i) => {
        const isCompleted = i < loadingStep;
        const isCurrent = i === loadingStep;
        const isPending = i > loadingStep;

        return (
          <div
            key={i}
            className="flex items-center gap-2.5 transition-opacity duration-300"
            style={{ opacity: isPending ? 0.3 : 1 }}
          >
            {isCompleted && (
              <span
                className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-[13px]"
                style={{ color: "var(--color-success)" }}
              >
                &#10003;
              </span>
            )}
            {isCurrent && (
              <span
                className="flex-shrink-0 w-4 h-4 border-2 rounded-full animate-spin"
                style={{
                  borderColor: "var(--ish-accent-border)",
                  borderTopColor: "var(--ish-accent)",
                }}
              />
            )}
            {isPending && (
              <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: "var(--ish-text-faint)" }}
                />
              </span>
            )}
            <span
              className="text-[13px]"
              style={{
                color: isCompleted
                  ? "var(--color-success)"
                  : isCurrent
                    ? "var(--ish-dark)"
                    : "var(--ish-text-faint)",
                fontWeight: isCurrent ? 500 : 400,
              }}
            >
              {language === "hi" ? step.hi : step.en}
            </span>
          </div>
        );
      })}
    </div>
  );
}
